type ReleaseMetadata = Readonly<{
  major: number
  release: string | null
  version: string
}>

export type AppUpdateStatus = Readonly<{
  available: boolean
  required: boolean
}>

function isReleaseMetadata(value: unknown): value is ReleaseMetadata {
  if (!value || typeof value !== 'object') return false

  const metadata = value as Partial<ReleaseMetadata>
  return (
    Number.isSafeInteger(metadata.major) &&
    Number(metadata.major) >= 0 &&
    (metadata.release === null || typeof metadata.release === 'string') &&
    typeof metadata.version === 'string'
  )
}

async function fetchReleaseMetadata(
  signal?: AbortSignal,
  fetcher: typeof fetch = fetch,
): Promise<ReleaseMetadata | null> {
  const requestOptions: RequestInit = {
    cache: 'no-store',
    headers: { accept: 'application/json' },
  }
  if (signal) requestOptions.signal = signal

  const response = await fetcher(
    `/app-version.json?time=${String(Date.now())}`,
    requestOptions,
  )

  if (!response.ok) return null

  const metadata: unknown = await response.json()
  if (!metadata || typeof metadata !== 'object') return null

  const candidate = metadata as Partial<ReleaseMetadata>
  const normalized = {
    ...candidate,
    release: typeof candidate.release === 'string' ? candidate.release : null,
  }
  return isReleaseMetadata(normalized) ? normalized : null
}

export async function getAppUpdateStatus(
  signal?: AbortSignal,
  fetcher: typeof fetch = fetch,
): Promise<AppUpdateStatus> {
  try {
    const metadata = await fetchReleaseMetadata(signal, fetcher)
    if (!metadata) return { available: false, required: false }

    return {
      available:
        metadata.release !== null &&
        metadata.release !== __CARDREPLAY_RELEASE_ID__,
      required: metadata.major > __CARDREPLAY_UPDATE_MAJOR__,
    }
  } catch {
    return { available: false, required: false }
  }
}

export async function isRequiredMajorUpdate(
  signal?: AbortSignal,
  fetcher: typeof fetch = fetch,
) {
  try {
    const metadata = await fetchReleaseMetadata(signal, fetcher)
    return metadata?.major !== undefined
      ? metadata.major > __CARDREPLAY_UPDATE_MAJOR__
      : false
  } catch {
    return false
  }
}
